import { Suspense, useState, useCallback, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Spinner } from '../ui/Spinner';
import { StagingBanner } from '../ui/StagingBanner';
import { HqOwlWait } from '../ui/HqOwlWait';

const COLLAPSED_KEY = 'ruby-hq.sidebar.collapsed';

export function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop-only collapse state (md+). Independent of the mobile drawer
  // open/close flow above. Persisted to localStorage so admin preference
  // survives reloads.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  });
  const toggleSidebar = useCallback(() => setSidebarOpen(v => !v), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleCollapsed = useCallback(() => setCollapsed(v => !v), []);

  useEffect(() => {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  return (
    // `data-sidebar-collapsed` lets viewport-fixed overlays (e.g. the bulk
    // action bar) offset their left edge to the content column without prop-
    // drilling the collapse state — see `.bulk-bar-wrap` in index.css.
    <div className="flex flex-col h-screen bg-gray-50" data-sidebar-collapsed={collapsed ? '1' : '0'}>
      <StagingBanner />
      <div className="flex flex-1 min-h-0">
      {/* Backdrop — visible only on mobile when sidebar is open */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={closeSidebar}
        />
      )}

      <Sidebar
        open={sidebarOpen}
        onClose={closeSidebar}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Header onMenuToggle={toggleSidebar} />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Suspense fallback={<PageLoading />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
      </div>
    </div>
  );
}

/** A lazy page loading: the owl opening up. (The sign-in thumbs-up plays on
 *  the sidebar's gem.) Plan: docs/plans/2026-10-22-owl-waits-everywhere.md */
function PageLoading() {
  return (
    <div className="flex items-center justify-center h-64">
      <HqOwlWait kind="open" size={96} label="Loading" fallback={<Spinner size="lg" />} />
    </div>
  );
}
